import {
    AlertCircle,
    AlertTriangle,
    CheckCircle,
    Info,
    X,
} from "lucide-preact";
import type {
    ComponentChildren,
    JSX,
} from "preact";
import {
    forwardRef,
} from "preact/compat";

type AlertVariant = "success" | "error" | "warning" | "info";

type AlertProps = JSX.HTMLAttributes<HTMLDivElement> & {
    variant?: AlertVariant;
    children: ComponentChildren;
    onDismiss?: () => void;
};

const variantStyles: Record<AlertVariant, string> = {
    success: "bg-success/10 border-success/30 text-success",
    error: "bg-error/10 border-error/30 text-error",
    warning: "bg-warning/10 border-warning/30 text-warning",
    info: "bg-accent/10 border-accent/30 text-accent",
};

const variantIcons: Record<AlertVariant, typeof CheckCircle> = {
    success: CheckCircle,
    error: AlertCircle,
    warning: AlertTriangle,
    info: Info,
};

export const Alert = forwardRef<HTMLDivElement, AlertProps>(
    ({
        variant = "info",
        className = "",
        children,
        onDismiss,
        ...props
    }, ref) => {
        const Icon = variantIcons[variant];

        return (
            <div
                ref={ref}
                role="alert"
                className={`flex items-start gap-3 p-4 rounded-lg border ${
                    variantStyles[variant]
                } ${className}`}
                {...props}
            >
                <Icon className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <div className="flex-1 text-sm">
                    {children}
                </div>
                {onDismiss && (
                    <button
                        type="button"
                        onClick={onDismiss}
                        className="flex-shrink-0 p-1 rounded hover:bg-white/10 transition-colors"
                        aria-label="Dismiss alert"
                    >
                        <X className="w-4 h-4" />
                    </button>
                )}
            </div>
        );
    },
);
